/**
 * @fileoverview Contexto de acessibilidade e temas da aplicação.
 * Gerencia a troca dinâmica de temas para melhorar a acessibilidade.
 * 
 * @module AccessibilityContext
 * @since 1.0.0
 */

import { createContext, useContext, useState } from 'react';
import { themes } from '../components/Theme/Theme';

/**
 * Contexto para gerenciamento de temas.
 * @type {React.Context}
 */
const ThemeContext = createContext();

/**
 * Hook para acessar o contexto de temas.
 * Deve ser usado dentro de um CustomThemeProvider.
 * 
 * @returns {Object} Contexto do tema com funções de controle
 * @throws {Error} Quando usado fora do ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  }
  return context;
};

/**
 * Provedor de contexto para temas customizados.
 * Permite troca dinâmica entre diferentes temas de acessibilidade.
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {JSX.Element} Provedor de contexto configurado
 */
export const CustomThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('default');

  /**
   * Altera o tema atual da aplicação.
   * @param {string} themeName - Nome do tema a ser aplicado
   */
  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
  };

  /**
   * Obtém o tema atual ou retorna o tema padrão.
   * @returns {import('@mui/material/styles').Theme} Tema Material-UI
   */
  const getTheme = () => themes[currentTheme] || themes.default;

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      changeTheme, 
      getTheme,
      availableThemes: Object.keys(themes)
    }}>
      {children}
    </ThemeContext.Provider>
  );
};